import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ShoppingCart, Clock3 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import {
  loadMsg91Widget,
  msg91AccessToken,
  resendMsg91Sms,
  sendMsg91Otp,
  verifyMsg91Otp,
} from "../lib/msg91Otp";
import { PhoneProfileCompletion } from "../components/PhoneProfileCompletion";

const GROCERY_BAG =
  "https://static.prod-images.emergentagent.com/jobs/e874d64a-aa18-4706-8d24-2453b99645df/images/9753ad7831a8726c765cf5e38a77d333b49b0eb393fbae768094c424ff4f91d8.jpeg";
const OTP_ART =
  "https://static.prod-images.emergentagent.com/jobs/e874d64a-aa18-4706-8d24-2453b99645df/images/1b82544e26a541b153a74cdbe11f975c3720241c6a1cf35aba1483eff454b1ac.jpeg";

function destForRole(role, next) {
  if (role === "admin") return "/admin";
  if (role === "store_owner") return "/store";
  if (role === "rider") return "/rider";
  return next;
}

function Wordmark({ center }) {
  return (
    <div className={`flex items-center gap-2 ${center ? "justify-center" : ""}`}>
      <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#FF5200] shadow-sm">
        <ShoppingCart size={20} className="text-white" strokeWidth={2.5} />
      </span>
      <div className="leading-none">
        <p className="text-xl font-black tracking-tight text-slate-900">
          Quick<span className="text-[#FF5200]">Door</span>Now
        </p>
        <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
          Food | Grocery | Essentials
        </p>
      </div>
    </div>
  );
}

export default function Login() {
  const {
    login,
    error,
    setError,
    completeMsg91PhoneLogin,
    completePhoneProfile,
  } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/";

  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [showStaff, setShowStaff] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg91Ready, setMsg91Ready] = useState(false);

  useEffect(() => {
    setError("");
  }, [step, setError]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  useEffect(() => {
    let active = true;
    loadMsg91Widget()
      .then(() => active && setMsg91Ready(true))
      .catch((loadError) => active && setError(loadError.message));
    return () => {
      active = false;
    };
  }, [setError]);

  const phoneValid = useMemo(() => /^[6-9]\d{9}$/.test(phone.replace(/\D/g, "")), [phone]);
  const otpValue = otp.join("");

  const requestOtp = async (isResend = false) => {
    setBusy(true);
    setError("");
    try {
      if (isResend) {
        await resendMsg91Sms();
      } else {
        await sendMsg91Otp(phone.replace(/\D/g, ""));
      }
      setStep("otp");
      setOtp(["", "", "", "", "", ""]);
      setResendIn(60);
      toast.success(isResend ? "SMS code resent" : "SMS code sent to your phone");
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError("");
    try {
      const msg91Response = await verifyMsg91Otp(otpValue);
      const accessToken = msg91AccessToken(msg91Response);
      if (!accessToken) {
        throw new Error("SMS verification did not return a valid access token.");
      }
      const u = await completeMsg91PhoneLogin(accessToken);
      if (u.needs_profile_completion) {
        setStep("profile");
        return;
      }
      toast.success(u.name ? `Welcome, ${u.name}!` : "Welcome to Quick Door Now!");
      nav(destForRole(u.role, next));
    } catch (e) {
      setError(formatApiError(e));
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  };

  const completeProfile = async (payload) => {
    setBusy(true);
    setError("");
    try {
      const u = await completePhoneProfile(payload);
      toast.success(`Welcome, ${u.name}!`);
      nav(destForRole(u.role, next));
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setOtp((prev) => {
      const nextOtp = [...prev];
      nextOtp[index] = digit;
      return nextOtp;
    });
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const onOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus();
  };

  const onOtpPaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const arr = ["", "", "", "", "", ""];
    for (let i = 0; i < text.length; i += 1) arr[i] = text[i];
    setOtp(arr);
    otpRefs.current[Math.min(text.length, 5)]?.focus();
  };

  const staffLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}!`);
      nav(destForRole(u.role, next));
    } catch {
      /* error set in context */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-[#FCF7F0] px-5 pb-10 pt-8" data-testid="login-screen">
      {/* decorative dashes */}
      <div className="pointer-events-none absolute left-4 top-10 space-y-1.5 opacity-70">
        <div className="h-1 w-8 -rotate-12 rounded-full bg-[#FF5200]" />
        <div className="h-1 w-5 -rotate-12 rounded-full bg-orange-300" />
      </div>

      <div className="relative mx-auto w-full max-w-md">
        {step === "phone" && (
          <>
            <div className="pt-2">
              <Wordmark />
            </div>

            <h1 className="mt-10 text-4xl font-black leading-tight tracking-tight text-slate-900">
              <span className="text-[#FF5200]">Log in</span> or sign up
            </h1>
            <p className="mt-2 text-[15px] leading-6 text-slate-500">
              Get your favourite food &amp; groceries delivered at your doorstep
            </p>

            <div className="mt-7">
              <div
                className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-3.5 transition-colors"
                data-testid="login-phone-field"
              >
                <span
                  className="flex shrink-0 select-none items-center gap-1 pr-1 text-sm font-bold text-slate-800"
                  data-testid="login-phone-country-code"
                >
                  🇮🇳 +91
                </span>
                <input
                  data-testid="login-phone-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="Enter mobile number"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && phoneValid && msg91Ready) {
                      requestOtp(false);
                    }
                  }}
                  aria-invalid={Boolean(error)}
                  disabled={busy}
                  style={{ border: "none", outline: "none", boxShadow: "none" }}
                  className="login-phone-input min-w-0 flex-1 appearance-none border-0 border-none bg-transparent p-0 text-sm font-semibold shadow-none outline-none ring-0 placeholder:font-normal placeholder:text-slate-400 focus:border-0 focus:border-none focus:shadow-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:outline-none disabled:cursor-not-allowed"
                />
              </div>
            </div>

            {error && (
              <p data-testid="login-error" className="mt-3 rounded-xl bg-red-50 p-2.5 text-xs font-medium text-red-700">
                {error}
              </p>
            )}

            <button
              data-testid="login-continue-btn"
              onClick={() => requestOtp(false)}
              disabled={!phoneValid || busy || !msg91Ready}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#FF5200] py-4 text-[15px] font-extrabold text-white shadow-lg shadow-orange-500/25 transition-transform active:scale-[0.99] disabled:opacity-50"
            >
              {busy ? <Loader2 className="animate-spin" size={18} /> : <>Continue&nbsp;→</>}
            </button>

            <div className="relative py-5 text-center">
              <span className="relative z-10 bg-[#FCF7F0] px-3 text-[11px] font-bold uppercase tracking-widest text-slate-400">
                or
              </span>
              <div className="absolute left-0 right-0 top-1/2 h-px bg-slate-200" />
            </div>

            <button
              type="button"
              data-testid="google-signin-btn"
              onClick={() => {
                const redirectUrl = window.location.origin + "/";
                window.location.href =
                  "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
              }}
              className="flex w-full items-center justify-center gap-2.5 rounded-2xl border border-slate-200 bg-white py-4 text-[15px] font-bold text-slate-800 shadow-sm transition-colors hover:bg-slate-50"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.5 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.5 13.2 17.8 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.9 24.6c0-1.6-.1-2.7-.4-3.9H24v7.4h12.9c-.3 2-1.7 5-4.9 7.1l7.6 5.9c4.5-4.2 7.3-10.3 7.3-16.5z" />
                <path fill="#FBBC05" d="M10.5 28.6a14.5 14.5 0 0 1 0-9.2l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.6 2.6 10.7l7.9-6.1z" />
                <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2 1.4-4.8 2.4-8.3 2.4-6.2 0-11.5-3.7-13.5-9.1l-7.9 6.1C6.5 42.6 14.6 48 24 48z" />
              </svg>
              Continue with Google
            </button>

            <p className="mt-5 text-center text-[11px] leading-5 text-slate-400">
              By continuing, you agree to our{" "}
              <span className="font-semibold text-[#FF5200]">Terms of Service</span> &amp;{" "}
              <span className="font-semibold text-[#FF5200]">Privacy Policy</span>
            </p>

            <div className="mt-4 text-center">
              <button
                type="button"
                data-testid="staff-login-toggle"
                onClick={() => setShowStaff((v) => !v)}
                className="text-xs font-bold text-slate-500 hover:text-slate-800"
              >
                {showStaff ? "Hide staff / email login" : "Staff or admin? Email login"}
              </button>
              {showStaff && (
                <form onSubmit={staffLogin} className="mx-auto mt-3 max-w-sm space-y-2 text-left" data-testid="staff-login-form">
                  <input
                    data-testid="login-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    required
                    placeholder="Email"
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF5200]"
                  />
                  <input
                    data-testid="login-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    type="password"
                    required
                    placeholder="Password"
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF5200]"
                  />
                  <button
                    data-testid="login-submit"
                    disabled={busy}
                    className="w-full rounded-xl bg-slate-900 py-2.5 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
                  >
                    {busy ? "Signing in..." : "Sign in"}
                  </button>
                </form>
              )}
            </div>

            <img
              src={GROCERY_BAG}
              alt=""
              aria-hidden="true"
              className="pointer-events-none mt-6 w-40 select-none opacity-95 mix-blend-multiply"
            />
          </>
        )}

        {step === "otp" && (
          <>
            <button
              type="button"
              data-testid="otp-change-number"
              onClick={() => setStep("phone")}
              className="mb-2 inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-slate-800"
            >
              <ChevronLeft size={16} /> Back
            </button>
            <div className="mt-1">
              <Wordmark center />
            </div>

            <img src={OTP_ART} alt="" aria-hidden="true" className="mx-auto mt-4 h-36 w-auto object-contain mix-blend-multiply" />

            <h1 className="mt-2 text-center text-[26px] font-black tracking-tight text-slate-900">
              <span className="text-[#FF5200]">Verify</span> your phone number
            </h1>
            <p className="mt-1 text-center text-sm text-slate-500">
              We&apos;ve sent a 6-digit verification code to
            </p>
            <p className="text-center text-base font-black text-slate-900">+91 {phone}</p>

            <div className="mt-5 flex justify-center gap-2.5" onPaste={onOtpPaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (otpRefs.current[index] = el)}
                  data-testid={`otp-box-${index}`}
                  value={digit}
                  onChange={(e) => onOtpChange(index, e.target.value)}
                  onKeyDown={(e) => onOtpKeyDown(index, e)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  className={`h-14 w-12 rounded-2xl border-2 bg-white text-center text-xl font-black outline-none transition-colors ${
                    error
                      ? "border-red-300 text-red-700"
                      : digit
                        ? "border-[#FF5200] text-slate-900"
                        : "border-slate-200 text-slate-900 focus:border-[#FF5200]"
                  }`}
                />
              ))}
            </div>

            {error && (
              <p data-testid="otp-error" className="mx-auto mt-3 max-w-sm rounded-xl bg-red-50 p-2.5 text-center text-xs font-medium text-red-700">
                {error}
              </p>
            )}

            <button
              data-testid="otp-verify-btn"
              onClick={verify}
              disabled={otpValue.length !== 6 || busy}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#FF5200] py-4 text-[15px] font-extrabold text-white shadow-lg shadow-orange-500/25 transition-transform active:scale-[0.99] disabled:opacity-50"
            >
              {busy ? <Loader2 className="animate-spin" size={18} /> : "Verify"}
            </button>

            <p className="mt-5 text-center text-sm font-semibold text-slate-500">Didn&apos;t receive the code?</p>
            <div className="mt-2 flex justify-center">
              {resendIn > 0 ? (
                <span
                  data-testid="otp-resend-timer"
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-4 py-2 text-sm font-bold text-slate-400"
                >
                  <Clock3 size={14} /> Resend OTP ({resendIn}s)
                </span>
              ) : (
                <button
                  type="button"
                  data-testid="otp-resend-btn"
                  onClick={() => requestOtp(true)}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 px-4 py-2 text-sm font-bold text-[#FF5200] hover:bg-orange-100"
                >
                  <Clock3 size={14} /> Resend OTP
                </button>
              )}
            </div>
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={() => setStep("phone")}
                className="text-sm font-extrabold text-[#FF5200] hover:underline"
              >
                Change phone number
              </button>
            </div>
          </>
        )}

        {step === "profile" && (
          <PhoneProfileCompletion onComplete={completeProfile} busy={busy} error={error} />
        )}
      </div>

      {step === "phone" && (
        <p className="relative mt-4 text-center text-xs text-slate-400">
          New here? Just enter your number — we&apos;ll create your account.{" "}
          <Link to="/register" className="font-semibold text-slate-600 hover:underline">
            Email sign up
          </Link>
        </p>
      )}
    </div>
  );
}
